<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Events"
      subtitle="Manage events and activities"
      icon="fas fa-calendar-star"
      gradient="green"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search title, location, organizer"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="filterActive"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.active"
            input-id="filterActive"
            label="name"
            :options="activeOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Event
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(event_date)="{ item }">
        {{ dateShortFormatter(item.event_date) }}
      </template>

      <template #cell(schedule)="{ item }">
        {{ item.start_time }} - {{ item.end_time }}
      </template>

      <template #cell(image_url)="{ item }">
        <img
          v-if="item.image_url"
          :src="resolveStoragePath(item.image_url)"
          alt="event"
          style="width: 48px; height: 48px; object-fit: cover; border-radius: 8px;"
        >
        <span
          v-else
          class="text-muted small"
        >No image</span>
      </template>

      <template #cell(is_active)="{ item }">
        <BBadge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Event' : 'Add Event'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="title"
                name="title"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Title"
                  label-for="fTitle"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fTitle"
                    v-model="formData.title"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="organized_by"
                name="organized by"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Organized By"
                  label-for="fOrganizer"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fOrganizer"
                    v-model="formData.organized_by"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="event_date"
                name="event date"
                rules="required"
              >
                <BFormGroup
                  label="Event Date"
                  label-for="fDate"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fDate"
                    v-model="formData.event_date"
                    type="date"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="3"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="start_time"
                name="start time"
                rules="required"
              >
                <BFormGroup
                  label="Start Time"
                  label-for="fStartTime"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fStartTime"
                    v-model="formData.start_time"
                    type="time"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="3"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="end_time"
                name="end time"
                rules="required"
              >
                <BFormGroup
                  label="End Time"
                  label-for="fEndTime"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fEndTime"
                    v-model="formData.end_time"
                    type="time"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="location"
                name="location"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Location"
                  label-for="fLocation"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fLocation"
                    v-model="formData.location"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="description"
                name="description"
                rules="required|min:10"
              >
                <BFormGroup
                  label="Description"
                  label-for="fDescription"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fDescription"
                    v-model="formData.description"
                    rows="4"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <BFormGroup
                label="Image (Optional)"
                label-for="fImage"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormFile
                  id="fImage"
                  accept="image/*"
                  :disabled="state.busy"
                  @change="handleImageUpload"
                />
                <small class="text-muted">Allowed: JPG, PNG, GIF, WEBP (max 5MB)</small>

                <div
                  v-if="eventImagePreview"
                  class="mt-2"
                >
                  <img
                    :src="eventImagePreview"
                    alt="preview"
                    style="width: 120px; height: 80px; object-fit: cover; border-radius: 8px;"
                  >
                </div>

                <BFormCheckbox
                  v-if="state.editing && formData.image_url"
                  id="remove_event_image"
                  v-model="formData.remove_image"
                  class="mt-2"
                  :disabled="state.busy"
                  :value="1"
                  :unchecked-value="0"
                >
                  Remove current image
                </BFormCheckbox>
              </BFormGroup>
            </BCol>

            <BCol cols="12">
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formData.is_active"
                  switch
                  value="1"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminEvent } from '@/services'

export default {
  name: 'VAdminEvents',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter, MxImageUrl],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Events' }),

  data () {
    return {
      state: { busy: false, editing: false },
      eventImagePreview: '',
      formData: {
        id: '',
        title: '',
        description: '',
        event_date: '',
        start_time: '',
        end_time: '',
        location: '',
        organized_by: '',
        image: null,
        image_url: '',
        remove_image: 0,
        is_active: 1
      },
      activeOptions: [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' }
      ],
      tableData: {
        busy: false,
        selected: {
          active: null
        },
        filter: {
          search: '',
          is_active: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'event_date',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'image_url', label: 'Image', class: 'text-center' },
          { mobile: 1, key: 'title', class: 'min-w-175px' },
          { mobile: 2, key: 'event_date', class: 'min-w-100px' },
          { mobile: 3, key: 'schedule', label: 'Time', class: 'min-w-120px' },
          { mobile: 4, key: 'location', class: 'min-w-175px' },
          { mobile: 5, key: 'organized_by', class: 'min-w-150px' },
          { mobile: 6, key: 'is_active', class: 'text-center' }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'tableData.selected.active' (active) {
      this.tableData.filter.is_active = active !== null && active !== undefined ? active.id : ''
    }
  },

  methods: {
    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        title: filter.search,
        location: filter.search,
        organized_by: filter.search,
        is_active: filter.is_active
      }

      return SAdminEvent.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    createItem () {
      this.state.editing = false
      this.eventImagePreview = ''
      this.formData = {
        id: '',
        title: '',
        description: '',
        event_date: '',
        start_time: '',
        end_time: '',
        location: '',
        organized_by: '',
        image: null,
        image_url: '',
        remove_image: 0,
        is_active: 1
      }
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.eventImagePreview = item.image_url ? this.resolveStoragePath(item.image_url) : ''
      this.formData = {
        id: item.id,
        title: item.title,
        description: item.description,
        event_date: item.event_date,
        start_time: item.start_time,
        end_time: item.end_time,
        location: item.location,
        organized_by: item.organized_by,
        image: null,
        image_url: item.image_url || '',
        remove_image: 0,
        is_active: item.is_active ? 1 : 0
      }
      this.$refs.modalForm.show()
    },

    handleImageUpload (event) {
      const file = event?.target?.files?.[0] || null
      this.formData.image = file
      this.formData.remove_image = 0

      if (!file) {
        this.eventImagePreview = this.formData.image_url ? this.resolveStoragePath(this.formData.image_url) : ''
        return
      }

      this.eventImagePreview = URL.createObjectURL(file)
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async validForm => {
        if (!validForm) return this.swalInvalid()

        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this event?</h6>' : '<h6>Create this event?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true

            const action = this.state.editing
              ? SAdminEvent.update(this.formData.id, this.formData)
              : SAdminEvent.store(this.formData)

            return action.then(() => {
              this.$refs.modalForm.hide()
              this.$refs.tableData.refetch()
              this.swalSuccess(`Event ${this.state.editing ? 'updated' : 'created'} successfully`)
            }).catch(({ message }) => {
              if (message?.errors) {
                this.$refs.formData.setErrors(message.errors)
              }
            }).finally(() => {
              this.state.busy = false
            })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete event',
        text: `Are you sure you want to delete "${item.title}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        SAdminEvent.destroy(item.id).then(() => {
          this.swalSuccess('Event deleted successfully')
          this.$refs.tableData.refetch()
        }).catch(error => {
          this.swalError(error.response?.data?.message || 'Failed to delete event')
        }).finally(() => {
          this.state.busy = false
        })
      }
    }
  }
}
</script>
